import { useEffect, useRef, useState, useId } from "react";
import type { ReactNode } from "react";
import {
  ArrowClockwise,
  CaretLeft,
  CaretRight,
  Plus,
  X,
} from "@phosphor-icons/react";
import { api, modes } from "./api";
import { Chart } from "./Chart";
import type {
  Page,
  Question,
  QuestionDetail,
  QuestionSet,
  Schedule,
  Report,
  Overview,
  Analytics,
} from "./admin-types";
import "./admin.css";

const tabs = [
  ["overview", "운영 개요"],
  ["questions", "문제은행"],
  ["sets", "세트 관리"],
  ["tutorial", "체험 지정"],
  ["schedule", "출제 일정"],
  ["reports", "신고 관리"],
];
const statusName: Record<string, string> = {
  approved: "승인",
  pending: "검수 대기",
  invalid: "무효",
};
const formatTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" })
    : "기록 없음";
const rate = (value: number | null) =>
  value === null ? "집계 대상 없음" : `${(value * 100).toFixed(1)}%`;
function useData<T>(url: string | null, revision = 0) {
  const key = `${url}:${revision}`;
  const [state, setState] = useState<{ key: string; data?: T; error?: string }>(
    { key: "" },
  );
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!url) return;
    let active = true;
    setState({ key });
    api<T>(url)
      .then((data) => {
        if (active) setState({ key, data });
      })
      .catch((e: Error) => {
        if (active) setState({ key, error: e.message });
      });
    return () => {
      active = false;
    };
  }, [url, key, retry]);
  return {
    data: state.key === key ? state.data : undefined,
    error: state.key === key ? state.error : undefined,
    retry: () => setRetry((x) => x + 1),
  };
}
function LoadState({ error, retry }: { error?: string; retry: () => void }) {
  return (
    <div className="admin-load" role={error ? "alert" : "status"}>
      {error || "불러오는 중…"}
      {error && (
        <button className="btn outline compact" onClick={retry}>
          다시 시도
        </button>
      )}
    </div>
  );
}
function Dialog({
  title,
  children,
  close,
  error,
  pending = false,
}: {
  title: string;
  children: ReactNode;
  close: () => void;
  error?: string;
  pending?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null),
    id = useId();
  useEffect(() => {
    const d = ref.current;
    d?.showModal();
    return () => d?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      aria-labelledby={id}
      className="admin-dialog"
      onCancel={(e) => {
        e.preventDefault();
        if (!pending) close();
      }}
    >
      <div className="modal-head">
        <h2 id={id}>{title}</h2>
        <button
          className="icon-btn"
          aria-label="닫기"
          disabled={pending}
          onClick={close}
        >
          <X size={22} />
        </button>
      </div>
      {error && (
        <p role="alert" className="error-banner">
          {error}
        </p>
      )}
      {children}
    </dialog>
  );
}
function Pager({
  data,
  change,
}: {
  data: Pick<Page<unknown>, "page" | "pageSize" | "total" | "totalPages">;
  change: (value: Record<string, string>) => void;
}) {
  const pages = [
    ...new Set([
      1,
      ...Array.from({ length: 5 }, (_, i) => data.page + i - 2).filter(
        (p) => p > 0 && p <= data.totalPages,
      ),
      data.totalPages,
    ]),
  ].sort((a, b) => a - b);
  return (
    <div className="admin-pagination">
      <span>
        {data.total.toLocaleString()}개 중{" "}
        {data.total ? (data.page - 1) * data.pageSize + 1 : 0}–
        {Math.min(data.page * data.pageSize, data.total)}
      </span>
      <nav aria-label="목록 페이지">
        <button
          aria-label="이전 페이지"
          disabled={data.page <= 1}
          onClick={() => change({ page: String(data.page - 1) })}
        >
          <CaretLeft />
        </button>
        {pages.map((p, i) => (
          <span key={p}>
            {i > 0 && p - pages[i - 1] > 1 && (
              <span className="page-gap">…</span>
            )}
            <button
              aria-label={`${p}페이지`}
              aria-current={p === data.page ? "page" : undefined}
              onClick={() => change({ page: String(p) })}
            >
              {p}
            </button>
          </span>
        ))}
        <button
          aria-label="다음 페이지"
          disabled={data.page >= data.totalPages}
          onClick={() => change({ page: String(data.page + 1) })}
        >
          <CaretRight />
        </button>
      </nav>
      <select
        aria-label="페이지당 항목 수"
        value={data.pageSize}
        onChange={(e) => change({ pageSize: e.target.value, page: "1" })}
      >
        {[25, 50, 100].map((n) => (
          <option key={n} value={n}>
            {n}개씩
          </option>
        ))}
      </select>
    </div>
  );
}
function Search({
  value,
  change,
  label = "검색",
  placeholder = "검색어 입력",
}: {
  value: string;
  change: (v: string) => void;
  label?: string;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <form
      className="admin-search"
      onSubmit={(e) => {
        e.preventDefault();
        change(draft.trim());
      }}
    >
      <input
        aria-label={label}
        maxLength={200}
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <button className="btn outline compact">검색</button>
    </form>
  );
}
function Picker({
  kind,
  value,
  onChange,
  label,
  initialLabel,
}: {
  kind: "questions" | "sets";
  value: string;
  onChange: (id: string, label: string, item: Question | QuestionSet) => void;
  label: string;
  initialLabel?: string;
}) {
  const [open, setOpen] = useState(false),
    [query, setQuery] = useState({ search: "", page: "1", pageSize: "25" }),
    [chosen, setChosen] = useState(initialLabel || value);
  const url = open
    ? `/admin/${kind === "sets" ? "sets/page" : "questions"}?${new URLSearchParams({ ...query, eligible: "true" })}`
    : null;
  const data = useData<Page<Question | QuestionSet>>(url);
  return (
    <div className="admin-picker">
      <span>{label}</span>
      <button
        type="button"
        className="btn outline"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {value ? chosen || value : "검색하여 선택"} <CaretRight />
      </button>
      {open && (
        <div className="admin-picker-options">
          <Search
            value={query.search}
            change={(search) => setQuery({ ...query, search, page: "1" })}
          />
          {data.data ? (
            <>
              <div className="admin-picker-list">
                {data.data.items.map((item) => {
                  const id = "key" in item ? item.key : item.id;
                  const text =
                    "name" in item
                      ? `${item.name} · v${item.version}`
                      : `${item.company} · ${item.date} · ${item.title}`;
                  return (
                    <button
                      type="button"
                      key={id}
                      onClick={() => {
                        onChange(id, text, item);
                        setChosen(text);
                        setOpen(false);
                      }}
                    >
                      {text}
                    </button>
                  );
                })}
                {!data.data.total && <p>선택 가능한 항목이 없어요.</p>}
              </div>
              <Pager
                data={data.data}
                change={(patch) => setQuery({ ...query, ...patch })}
              />
            </>
          ) : (
            <LoadState error={data.error} retry={data.retry} />
          )}
        </div>
      )}
    </div>
  );
}
function Metrics({ data }: { data: Analytics }) {
  const max = Math.max(
    1,
    ...data.daily.flatMap((d) => [d.visitors, d.signups, d.players]),
  );
  return (
    <>
      <div className="admin-metrics">
        {[
          ["방문자", data.totals.visitors],
          ["페이지 조회", data.totals.views],
          ["신규 가입", data.totals.signups],
          ["직접 플레이 사용자", data.totals.players],
        ].map(([label, value]) => (
          <div className="panel" key={label}>
            <span>{label}</span>
            <strong>{Number(value).toLocaleString()}</strong>
          </div>
        ))}
      </div>
      <section className="panel">
        <h2>서비스 이용 추이</h2>
        <p className="caption">
          {data.start} ~ {data.today} · 한국 시간 · 방문자는 브라우저 식별자
          기준입니다.
        </p>
        <div className="admin-legend">
          <span>● 방문자</span>
          <span>● 가입</span>
          <span>● 직접 플레이</span>
        </div>
        <svg
          className="admin-trend"
          viewBox="0 0 720 180"
          role="img"
          aria-label="일별 방문자·가입·직접 플레이 추이. 상세 수치는 아래 표에서 확인할 수 있습니다."
        >
          {[0, 1, 2, 3].map((i) => (
            <g key={i}>
              <line
                x1="35"
                x2="710"
                y1={155 - i * 45}
                y2={155 - i * 45}
                stroke="#e3e8ed"
              />
              <text x="0" y={160 - i * 45} fontSize="11">
                {Math.round((max * i) / 3)}
              </text>
            </g>
          ))}
          {(["visitors", "signups", "players"] as const).map((key, i) => (
            <polyline
              key={key}
              points={data.daily
                .map(
                  (d, n) =>
                    `${35 + (n * 675) / Math.max(1, data.daily.length - 1)},${155 - (d[key] / max) * 135}`,
                )
                .join(" ")}
              fill="none"
              stroke={["#31745b", "#b77132", "#5274bb"][i]}
              strokeWidth="2.5"
            />
          ))}
          <text x="35" y="178" fontSize="11">
            {data.start}
          </text>
          <text x="640" y="178" fontSize="11">
            {data.today}
          </text>
        </svg>
        <details>
          <summary>일별 수치 보기</summary>
          <div className="admin-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>날짜</th>
                  <th>방문자</th>
                  <th>조회</th>
                  <th>가입</th>
                  <th>플레이 사용자</th>
                </tr>
              </thead>
              <tbody>
                {data.daily.map((d) => (
                  <tr key={d.day}>
                    <th>{d.day}</th>
                    <td>{d.visitors}</td>
                    <td>{d.views}</td>
                    <td>{d.signups}</td>
                    <td>{d.players}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      </section>
      <div className="admin-grid">
        <section className="panel">
          <h2>플레이 참여</h2>
          <div className="admin-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>모드</th>
                  <th>시작</th>
                  <th>직접 완주</th>
                  <th>완주율</th>
                  <th>자동 정산</th>
                </tr>
              </thead>
              <tbody>
                {data.modes.map((m) => (
                  <tr key={m.mode}>
                    <th>{modes[m.mode]}</th>
                    <td>{m.started}</td>
                    <td>{m.completed}</td>
                    <td>{rate(m.rate)}</td>
                    <td>{m.automatic}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="caption">
            기간 내 시작한 판의 현재 결과입니다. 직접 완주는 모든 문제를
            수동으로 답한 판이며 자동 정산과 구분합니다.
          </p>
          <h3>7일 내 다른 날 재플레이</h3>
          <strong className="admin-number">{rate(data.retention.rate)}</strong>
          <p>
            {data.retention.returned} / {data.retention.eligible}명
          </p>
          <p className="caption">
            전체 기록에서 첫 공식 수동 플레이 후 7일 관찰이 끝난 사용자
            기준입니다. 체험은 제외합니다.
          </p>
        </section>
        <section className="panel">
          <h2>방문에서 가입까지</h2>
          <ol className="admin-funnel">
            {["최초 방문", "체험 시작", "체험 직접 완료", "신규 가입"].map(
              (label, i) => (
                <li key={label}>
                  <span>{label}</span>
                  <strong>{data.funnel.steps[i].toLocaleString()}</strong>
                </li>
              ),
            )}
          </ol>
          <p>
            방문 → 가입 전환율 <strong>{rate(data.funnel.rate)}</strong>
          </p>
          <p className="caption">
            기간 내 최초 방문한 브라우저에서 7일 이내 위 순서로 진행한
            경우입니다. 관찰 중 {data.funnel.observing}개 · 기존 회원 로그인은
            제외합니다.
          </p>
        </section>
      </div>
      <p className="caption">
        방문·전환 수집 시작: {formatTime(data.trackingStarted)}. 수집 이전
        방문은 복원하지 않습니다. 플레이는 수동 답변일 기준이며{" "}
        {data.demo
          ? "데모 계정을 포함합니다."
          : "운영·시스템 계정은 제외합니다."}{" "}
        쿠키 삭제나 기기 변경으로 방문자가 달라질 수 있습니다.
      </p>
    </>
  );
}
type Props = {
  query: URLSearchParams;
  replace: (url: string) => void;
  notify: (message: string) => void;
  refresh: () => Promise<unknown>;
};
export function Admin({ query, replace, notify, refresh }: Props) {
  const tab = tabs.some(([id]) => id === query.get("tab"))
    ? query.get("tab")!
    : "overview";
  const [revision, setRevision] = useState(0),
    [pending, setPending] = useState(false),
    [error, setError] = useState("");
  const pendingRef = useRef(false);
  const [question, setQuestion] = useState<string | null>(null),
    [importing, setImporting] = useState(false),
    [editingSet, setEditingSet] = useState<QuestionSet | null>(null),
    [scheduling, setScheduling] = useState(false),
    [moderating, setModerating] = useState<{
      report: Report;
      action: string;
    } | null>(null);
  const overview = useData<Overview>("/admin/overview", revision);
  const analytics = useData<Analytics>(
    tab === "overview"
      ? `/admin/analytics?days=${query.get("days") || "30"}`
      : null,
    revision,
  );
  const facets = useData<Record<string, string[]>>(
    tab === "questions" ? "/admin/question-filters" : null,
    revision,
  );
  const listParams = new URLSearchParams();
  for (const key of [
    "search",
    "market",
    "year",
    "sector",
    "status",
    "retired",
    "eligible",
    "page",
    "pageSize",
  ])
    if (query.get(key)) listParams.set(key, query.get(key)!);
  const endpoints: Record<string, string> = {
    questions: "questions",
    sets: "sets/page",
    schedule: "schedules",
    reports: "reports",
  };
  const list = useData<Page<Question | QuestionSet | Schedule | Report>>(
    endpoints[tab] ? `/admin/${endpoints[tab]}?${listParams}` : null,
    revision,
  );
  const detail = useData<QuestionDetail>(
    question ? `/admin/questions/${encodeURIComponent(question)}` : null,
    revision,
  );
  const change = (patch: Record<string, string>) => {
    const next = new URLSearchParams(query);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    replace(`/admin?${next}`);
  };
  const navigate = (target: string, patch: Record<string, string> = {}) => {
    const next = new URLSearchParams({ tab: target, ...patch });
    if (query.get("days")) next.set("days", query.get("days")!);
    replace(`/admin?${next}`);
    setError("");
  };
  // Reflect the server's clamp after an item leaves the last filtered page.
  useEffect(() => {
    if (list.data && Number(query.get("page") || 1) !== list.data.page)
      change({ page: String(list.data.page) });
  }, [list.data]);
  const act = async (
    fn: () => Promise<unknown>,
    message: string,
    close?: () => void,
  ) => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    setError("");
    try {
      await fn();
      close?.();
      setRevision((x) => x + 1);
      notify(message);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "요청을 처리하지 못했어요.");
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };
  const openQuestion = (id: string) => {
    setError("");
    setQuestion(id);
  };
  if (!overview.data)
    return <LoadState error={overview.error} retry={overview.retry} />;
  const o = overview.data;
  return (
    <div className="admin-workspace">
      <div className="page-heading">
        <div>
          <div className="date-line">
            서비스와 콘텐츠 운영 {o.demo && "· 로컬 데모"}
          </div>
          <h1>운영 관리</h1>
          <p>이용 흐름을 살펴보고, 필요한 운영 작업을 이어가세요.</p>
        </div>
        <div className="admin-heading-actions">
          <button
            className="btn outline compact"
            disabled={pending}
            onClick={() => setRevision((x) => x + 1)}
          >
            <ArrowClockwise size={17} />
            새로고침
          </button>
          <button
            className="btn primary compact"
            onClick={() => {
              setError("");
              setImporting(true);
            }}
          >
            <Plus size={17} />
            후보 가져오기
          </button>
        </div>
      </div>
      <div className="filter-tabs admin-tabs">
        {tabs.map(([id, label]) => (
          <button
            key={id}
            className={tab === id ? "active" : ""}
            onClick={() => navigate(id)}
          >
            {label}
            {id === "reports" && o.reports.open > 0 && (
              <span className="admin-badge">{o.reports.open}</span>
            )}
          </button>
        ))}
      </div>
      {error &&
        !question &&
        !importing &&
        !editingSet &&
        !scheduling &&
        !moderating && (
          <p className="error-banner" role="alert">
            {error}
          </p>
        )}
      {tab === "overview" && (
        <>
          <div className="admin-section-heading">
            <h2>서비스 현황</h2>
            <label>
              조회 기간{" "}
              <select
                aria-label="통계 기간"
                value={query.get("days") || "30"}
                onChange={(e) => change({ days: e.target.value })}
              >
                {[7, 30, 90].map((n) => (
                  <option key={n} value={n}>
                    최근 {n}일
                  </option>
                ))}
              </select>
            </label>
          </div>
          {analytics.data ? (
            <Metrics data={analytics.data} />
          ) : (
            <LoadState error={analytics.error} retry={analytics.retry} />
          )}
          <div className="admin-section-heading">
            <h2>콘텐츠와 운영</h2>
            <span className="caption">집계: {formatTime(o.at)}</span>
          </div>
          <div className="admin-metrics">
            {(
              [
                ["eligible", "출제 가능"],
                ["pending", "검수 대기"],
                ["invalid", "무효"],
                ["retired", "보관"],
              ] as const
            ).map(([key, label]) => (
              <button
                className="panel admin-metric-link"
                key={key}
                onClick={() =>
                  navigate(
                    "questions",
                    key === "retired"
                      ? { retired: "retired" }
                      : key === "eligible"
                        ? { eligible: "true", status: "approved" }
                        : { status: key },
                  )
                }
              >
                <span>{label}</span>
                <strong>{o.questions[key].toLocaleString()}</strong>
              </button>
            ))}
          </div>
          <div className="admin-grid">
            <section className="panel">
              <h2>이번 주 출제 준비</h2>
              <div className="admin-week">
                {o.schedules.map((s) => (
                  <button
                    key={s.day}
                    className={s.ready ? "ready" : "missing"}
                    onClick={() => navigate("schedule")}
                  >
                    <span>{s.day.slice(5)}</span>
                    <strong>{s.ready ? "준비됨" : "확인 필요"}</strong>
                  </button>
                ))}
              </div>
              <p className="caption">
                한국 시간 오늘부터 7일 · 하루 3세트, 출제 가능한 서로 다른
                15문제 기준
              </p>
              <p>최근 자동 편성 성공: {formatTime(o.scheduler?.lastSuccess)}</p>
              {o.scheduler?.failures?.map((f) => (
                <p className="admin-warning" key={f.day}>
                  {f.day} · {f.error}
                </p>
              ))}
              <button
                className="btn outline compact"
                onClick={() => navigate("schedule")}
              >
                일정 확인
              </button>
            </section>
            <section className="panel">
              <h2>처리가 필요한 일</h2>
              <button
                className="admin-task"
                onClick={() => navigate("reports")}
              >
                <span>미처리 신고</span>
                <strong>
                  {o.reports.open}건 <CaretRight />
                </strong>
              </button>
              <p className="caption">
                가장 오래된 신고:{" "}
                {o.reports.oldestHours === null
                  ? "없음"
                  : `${o.reports.oldestHours}시간 대기`}
              </p>
              <button className="admin-task" onClick={() => navigate("sets")}>
                <span>저장된 세트 버전</span>
                <strong>
                  {o.setCount}개 <CaretRight />
                </strong>
              </button>
              <button
                className="admin-task"
                onClick={() => navigate("tutorial")}
              >
                <span>현재 체험</span>
                <strong>
                  {o.tutorialSet
                    ? `${o.tutorialSet.name} · v${o.tutorialSet.version}`
                    : "미지정"}
                </strong>
              </button>
              {o.tutorialSet && !o.tutorialSet.eligible && (
                <p className="admin-warning">
                  현재 체험 세트에 출제 불가 문제가 있습니다.
                </p>
              )}
            </section>
          </div>
          <div className="admin-distributions">
            {Object.entries(o.distributions).map(([key, rows]) => (
              <section className="panel" key={key}>
                <h2>
                  {{ market: "국가", year: "연도", sector: "섹터" }[key]}별 문제
                </h2>
                <p className="caption">보관 문제 제외</p>
                <div className="admin-distribution-list">
                  {rows.map((row) => (
                    <div key={row.label}>
                      <span>{row.label || "미분류"}</span>
                      <strong>{row.count.toLocaleString()}</strong>
                    </div>
                  ))}
                  {!rows.length && <p>등록된 문제가 없어요.</p>}
                </div>
              </section>
            ))}
          </div>
        </>
      )}
      {tab === "questions" && (
        <div className="admin-filters">
          <Search
            value={query.get("search") || ""}
            change={(search) => change({ search, page: "1" })}
            label="문제 검색"
            placeholder="기업·제목·상황·ID 검색"
          />
          {(["market", "year", "sector", "status"] as const).map((key) => (
            <select
              aria-label={
                {
                  market: "국가",
                  year: "연도",
                  sector: "섹터",
                  status: "검수 상태",
                }[key]
              }
              key={key}
              value={query.get(key) || ""}
              onChange={(e) =>
                change({ [key]: e.target.value, page: "1", eligible: "" })
              }
            >
              <option value="">
                모든{" "}
                {
                  {
                    market: "국가",
                    year: "연도",
                    sector: "섹터",
                    status: "검수 상태",
                  }[key]
                }
              </option>
              {facets.data?.[key]?.map((v) => (
                <option key={v} value={v}>
                  {key === "status" ? statusName[v] || v : v}
                </option>
              ))}
            </select>
          ))}
          <select
            aria-label="보관 여부"
            value={query.get("retired") || "active"}
            onChange={(e) => change({ retired: e.target.value, page: "1" })}
          >
            <option value="active">현재 문제</option>
            <option value="retired">보관 문제</option>
            <option value="all">전체 문제</option>
          </select>
          <button
            className="btn outline compact"
            onClick={() => navigate("questions")}
          >
            초기화
          </button>
          {facets.error && (
            <LoadState error={facets.error} retry={facets.retry} />
          )}
        </div>
      )}
      {tab === "sets" && (
        <div className="admin-section-heading">
          <Search
            value={query.get("search") || ""}
            change={(search) => change({ search, page: "1" })}
            label="세트 검색"
            placeholder="세트 이름·키 검색"
          />
          <button
            className="btn primary compact"
            onClick={() => {
              setError("");
              setEditingSet({
                id: "",
                key: "",
                name: "",
                version: 0,
                questionIds: [],
                eligible: true,
              });
            }}
          >
            새 세트
          </button>
        </div>
      )}
      {tab === "schedule" && (
        <div className="admin-section-heading">
          <div>
            <h2>출제 일정</h2>
            <p className="caption">
              오늘부터 7일 자동 편성 · 수동 편성은 미래 날짜만 가능
            </p>
            <p className="caption">
              최근 자동 편성 성공: {formatTime(o.scheduler?.lastSuccess)}
            </p>
            {o.scheduler?.failures?.map((f) => (
              <p className="admin-warning" key={f.day}>
                {f.day} · {f.error}
              </p>
            ))}
          </div>
          <button
            className="btn primary compact"
            onClick={() => {
              setError("");
              setScheduling(true);
            }}
          >
            미래 일정 편성
          </button>
        </div>
      )}
      {tab === "reports" && (
        <div className="admin-filters">
          <label>
            처리 상태{" "}
            <select
              value={query.get("status") || "open"}
              onChange={(e) => change({ status: e.target.value, page: "1" })}
            >
              <option value="open">미처리</option>
              <option value="resolved">처리 완료</option>
              <option value="all">전체</option>
            </select>
          </label>
        </div>
      )}
      {endpoints[tab] && (
        <section className="panel admin-list-panel">
          {list.data ? (
            <>
              <Pager data={list.data} change={change} />
              {!list.data.total && (
                <p className="admin-empty">조건에 맞는 항목이 없어요.</p>
              )}
              {tab === "questions" &&
                (list.data.items as Question[]).map((q) => (
                  <button
                    className="admin-question-row"
                    key={q.id}
                    onClick={() => openQuestion(q.id)}
                  >
                    <span className={`status-tag ${q.status}`}>
                      {q.retired ? "보관" : statusName[q.status] || q.status}
                    </span>
                    <span>
                      <strong>
                        {q.company} · {q.title}
                      </strong>
                      <small>
                        {q.market} · {q.sector} · {q.date} · {q.horizon}거래일 ·
                        v{q.version}
                      </small>
                    </span>
                    <CaretRight />
                  </button>
                ))}
              {tab === "sets" &&
                (list.data.items as QuestionSet[]).map((s) => (
                  <div className="admin-list-row" key={s.key}>
                    <div>
                      <strong>
                        {s.name} · v{s.version}
                      </strong>
                      <p className="caption">
                        {s.questions
                          ?.map((q, i) => q?.company || s.questionIds[i])
                          .join(" · ")}
                      </p>
                      {!s.eligible && (
                        <span className="admin-warning">
                          출제 불가 문제 포함
                        </span>
                      )}
                    </div>
                    <button
                      className="btn outline compact"
                      onClick={() => {
                        setError("");
                        setEditingSet(s);
                      }}
                    >
                      새 버전 만들기
                    </button>
                  </div>
                ))}
              {tab === "schedule" &&
                (list.data.items as Schedule[]).map((s) => (
                  <details className="schedule-preview" key={s.day}>
                    <summary>
                      <strong>{s.day}</strong> · {s.sets.length}세트 ·{" "}
                      {s.sets.flat().length}문제{" "}
                      <span className="tag">
                        {s.reissued ? "재출제 포함" : "신규 편성"}
                      </span>
                    </summary>
                    {s.sets.map((ids, i) => (
                      <section key={i}>
                        <h3>{i + 1}번째 판</h3>
                        {ids.map((id) => {
                          const q = s.questions.find((x) => x?.id === id);
                          return (
                            <button
                              className="admin-question-row"
                              key={id}
                              disabled={!q}
                              onClick={() => openQuestion(id)}
                            >
                              <span>
                                <strong>
                                  {q?.company || "보관된 문제"} ·{" "}
                                  {q?.title || id}
                                </strong>
                                <small>
                                  {q?.date} · {q?.sector}
                                </small>
                              </span>
                              <CaretRight />
                            </button>
                          );
                        })}
                      </section>
                    ))}
                  </details>
                ))}
              {tab === "reports" &&
                (list.data.items as Report[]).map((r) => (
                  <article className="admin-report" key={r.id}>
                    <div className="admin-section-heading">
                      <strong>
                        {r.kind === "posts"
                          ? "게시글"
                          : r.kind === "questionComments"
                            ? "문제 댓글"
                            : "댓글"}{" "}
                        신고
                      </strong>
                      <span className="caption">{formatTime(r.created)}</span>
                    </div>
                    <p>신고 사유: {r.reason}</p>
                    <blockquote>
                      {r.content ? (
                        <>
                          <strong>{r.content.title}</strong>
                          <p>{r.content.body}</p>
                          <small>
                            작성자: {r.author?.name || "계정 없음"}
                            {r.content.hidden ? " · 숨김" : ""}
                            {r.author?.suspended ? " · 이용 제한 중" : ""}
                          </small>
                        </>
                      ) : (
                        <p>대상이 없어 기각만 가능해요.</p>
                      )}
                    </blockquote>
                    {r.resolved ? (
                      <p className="caption">
                        처리 완료 ·{" "}
                        {{
                          hide: "콘텐츠 숨김",
                          suspend: "작성자 이용 제한",
                          dismiss: "기각",
                        }[r.action || ""] || "이전 처리 기록"}{" "}
                        · {formatTime(r.resolvedAt)}
                      </p>
                    ) : (
                      <div className="share-buttons">
                        {[
                          ["hide", "콘텐츠 숨김"],
                          ["suspend", "작성자 이용 제한"],
                          ["dismiss", "기각"],
                        ].map(([action, label]) => (
                          <button
                            key={action}
                            className="btn outline compact"
                            disabled={
                              pending ||
                              (action !== "dismiss" &&
                                (!r.content || !r.author)) ||
                              (action === "suspend" &&
                                r.author?.protected === true)
                            }
                            onClick={() => {
                              setError("");
                              setModerating({ report: r, action });
                            }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                  </article>
                ))}
              <Pager data={list.data} change={change} />
            </>
          ) : (
            <LoadState error={list.error} retry={list.retry} />
          )}
        </section>
      )}
      {tab === "tutorial" && (
        <section className="panel">
          <h2>체험 5문제 지정</h2>
          <p>
            현재:{" "}
            {o.tutorialSet
              ? `${o.tutorialSet.name} · v${o.tutorialSet.version}`
              : "미지정"}
          </p>
          <p className="caption">
            선택한 세트 버전을 고정합니다. 이미 시작한 체험은 기존 문제를
            유지합니다.
          </p>
          <TutorialEditor
            key={o.tutorialSet?.key || "none"}
            initial={o.tutorialSet}
            pending={pending}
            save={(key) =>
              act(
                () => api("/admin/tutorial", { key }),
                "체험 세트를 지정했어요.",
              )
            }
          />
        </section>
      )}
      {question && (
        <Dialog
          title="문제 검수"
          close={() => setQuestion(null)}
          error={error}
          pending={pending}
        >
          {detail.data ? (
            <QuestionEditor
              key={detail.data.id}
              q={detail.data}
              pending={pending}
              save={(body) =>
                act(
                  () =>
                    api(
                      `/admin/questions/${encodeURIComponent(question)}`,
                      body,
                      "PATCH",
                    ),
                  "문제를 저장했어요.",
                  () => setQuestion(null),
                )
              }
            />
          ) : (
            <LoadState error={detail.error} retry={detail.retry} />
          )}
        </Dialog>
      )}
      {importing && (
        <Dialog
          title="문제 후보 가져오기"
          close={() => setImporting(false)}
          error={error}
          pending={pending}
        >
          <form
            className="stack-form"
            onSubmit={(e) => {
              e.preventDefault();
              const text = String(new FormData(e.currentTarget).get("json"));
              void act(
                () => api("/admin/import", { questions: JSON.parse(text) }),
                "검수 대기로 등록했어요.",
                () => setImporting(false),
              );
            }}
          >
            <p className="caption">
              기존 문제는 새 ID로 등록합니다. 1~500개의 문제 JSON 배열을
              입력하세요.
            </p>
            <label>
              문제 JSON
              <textarea name="json" rows={10} required />
            </label>
            <button className="btn primary" disabled={pending}>
              가져오기
            </button>
          </form>
        </Dialog>
      )}
      {editingSet && (
        <Dialog
          title={editingSet.id ? "세트 새 버전" : "새 문제 세트"}
          close={() => setEditingSet(null)}
          error={error}
          pending={pending}
        >
          <SetEditor
            initial={editingSet}
            pending={pending}
            save={(body) =>
              act(
                () => api("/admin/sets", body),
                "세트를 저장했어요.",
                () => setEditingSet(null),
              )
            }
          />
        </Dialog>
      )}
      {scheduling && (
        <Dialog
          title="미래 출제 일정 편성"
          close={() => setScheduling(false)}
          error={error}
          pending={pending}
        >
          <ScheduleEditor
            today={o.today}
            pending={pending}
            save={(body) =>
              act(
                () => api("/admin/schedule", body),
                "출제 일정을 저장했어요.",
                () => setScheduling(false),
              )
            }
          />
        </Dialog>
      )}
      {moderating && (
        <Dialog
          title="신고 처리 확인"
          close={() => setModerating(null)}
          error={error}
          pending={pending}
        >
          <p>
            {
              {
                hide: "콘텐츠를 숨깁니다.",
                suspend: "작성자의 서비스 이용을 제한합니다.",
                dismiss: "신고를 기각합니다.",
              }[moderating.action]
            }
          </p>
          <blockquote>
            {moderating.report.content?.title ||
              moderating.report.content?.body ||
              "대상 없음"}
            <p>작성자: {moderating.report.author?.name || "계정 없음"}</p>
          </blockquote>
          <p>신고 사유: {moderating.report.reason}</p>
          <button
            className="btn danger"
            disabled={pending}
            onClick={() =>
              act(
                () =>
                  api(`/admin/reports/${moderating.report.id}`, {
                    action: moderating.action,
                  }),
                "신고를 처리했어요.",
                () => setModerating(null),
              )
            }
          >
            확인하고 처리
          </button>
        </Dialog>
      )}
    </div>
  );
}
function QuestionEditor({
  q,
  pending,
  save,
}: {
  q: QuestionDetail;
  pending: boolean;
  save: (body: Record<string, unknown>) => Promise<void>;
}) {
  const [draft, setDraft] = useState({
      title: q.title,
      context: q.context,
      explanation: q.explanation,
    }),
    [reason, setReason] = useState<string | null>(null),
    [note, setNote] = useState(q.reviewNote || "");
  const link = (url: string, label: string) =>
    /^https?:\/\//.test(url) ? (
      <a href={url} target="_blank" rel="noreferrer">
        {label} ↗
      </a>
    ) : (
      <span>출처 없음</span>
    );
  return (
    <div className="admin-preview">
      <span className="tag">
        {q.company} · {q.date} · {q.id}
      </span>
      <h3>{q.title}</h3>
      {q.editable ? (
        <div className="stack-form">
          {(["title", "context", "explanation"] as const).map((key) => (
            <label key={key}>
              {
                {
                  title: "제목",
                  context: "상황 설명",
                  explanation: "결과 해설",
                }[key]
              }
              <textarea
                maxLength={2000}
                required
                value={draft[key]}
                onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
              />
            </label>
          ))}
        </div>
      ) : (
        <>
          <p>{q.context}</p>
          <p className="caption">{q.editReason}</p>
        </>
      )}
      <Chart past={q.past} future={q.future} />
      <p>{q.explanation}</p>
      <dl>
        <dt>근거 공개일</dt>
        <dd>{q.sourceDate}</dd>
        <dt>원문 출처</dt>
        <dd>{link(q.source, "기업 발표 확인")}</dd>
        <dt>가격 데이터</dt>
        <dd>{link(q.priceSource, "원본 OHLCV 확인")}</dd>
        <dt>이용 상태</dt>
        <dd>{q.rights}</dd>
      </dl>
      {!q.invalid && (
        <>
          <label className="stack-form">
            검수 메모
            <textarea
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <div className="share-buttons">
            {q.editable && (
              <button
                className="btn outline"
                disabled={
                  pending || Object.values(draft).some((v) => !v.trim())
                }
                onClick={() =>
                  save({ ...draft, status: "pending", reviewNote: note })
                }
              >
                검수 대기로 저장
              </button>
            )}
            <button
              className="btn primary"
              disabled={
                pending ||
                (q.editable && Object.values(draft).some((v) => !v.trim()))
              }
              onClick={() =>
                save({
                  ...(q.editable ? draft : {}),
                  status: q.status === "approved" ? "pending" : "approved",
                  reviewNote: note,
                })
              }
            >
              {q.status === "approved" ? "검수 대기로 되돌리기" : "문제 승인"}
            </button>
            <button
              className="btn outline"
              disabled={pending}
              onClick={() => setReason("")}
            >
              오류로 무효 처리
            </button>
          </div>
        </>
      )}
      {reason !== null && (
        <form
          className="stack-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim()) void save({ invalid: true, reason });
          }}
        >
          <label>
            무효 처리 사유
            <textarea
              maxLength={500}
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <p className="caption">
            관련 플레이 기록을 재계산하고 사용자에게 알림을 보냅니다.
          </p>
          <button className="btn danger" disabled={pending || !reason.trim()}>
            무효 처리하고 성적 재계산
          </button>
        </form>
      )}
    </div>
  );
}
function TutorialEditor({
  initial,
  pending,
  save,
}: {
  initial: QuestionSet | null;
  pending: boolean;
  save: (key: string) => Promise<void>;
}) {
  const [key, setKey] = useState(initial?.key || "");
  return (
    <div className="stack-form">
      <Picker
        kind="sets"
        label="체험 세트"
        value={key}
        initialLabel={initial ? `${initial.name} · v${initial.version}` : ""}
        onChange={setKey}
      />
      <button
        className="btn primary"
        disabled={pending || !key}
        onClick={() => save(key)}
      >
        체험 지정
      </button>
    </div>
  );
}
function SetEditor({
  initial,
  pending,
  save,
}: {
  initial: QuestionSet;
  pending: boolean;
  save: (body: Record<string, unknown>) => Promise<void>;
}) {
  const [name, setName] = useState(initial.name),
    [ids, setIds] = useState(
      Array.from({ length: 5 }, (_, i) => initial.questionIds[i] || ""),
    );
  const valid = ids.every(Boolean) && new Set(ids).size === 5 && !!name.trim();
  return (
    <div className="stack-form">
      <label>
        세트 이름
        <input
          maxLength={100}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      {ids.map((id, i) => (
        <Picker
          key={i}
          kind="questions"
          label={`문제 ${i + 1}`}
          value={id}
          initialLabel={initial.questions?.[i]?.company}
          onChange={(value) =>
            setIds(ids.map((old, n) => (n === i ? value : old)))
          }
        />
      ))}
      <p className="caption">
        서로 다른 출제 가능 문제 5개를 선택하세요. 기존 버전은 유지됩니다.
      </p>
      <button
        className="btn primary"
        disabled={pending || !valid}
        onClick={() =>
          save({ id: initial.id || undefined, name, questionIds: ids })
        }
      >
        새 버전 저장
      </button>
    </div>
  );
}
function ScheduleEditor({
  today,
  pending,
  save,
}: {
  today: string;
  pending: boolean;
  save: (body: Record<string, unknown>) => Promise<void>;
}) {
  const tomorrow = new Date(Date.parse(today) + 86400000)
    .toISOString()
    .slice(0, 10);
  const [day, setDay] = useState(tomorrow),
    [keys, setKeys] = useState(["", "", ""]);
  const [selected, setSelected] = useState<Array<QuestionSet | null>>([
    null,
    null,
    null,
  ]);
  const allIds = selected.flatMap((s) => s?.questionIds || []);
  const duplicates = allIds.length !== new Set(allIds).size;
  const [checking, setChecking] = useState(false);
  const submit = async () => {
    setChecking(true);
    try {
      await save({ day, setKeys: keys });
    } finally {
      setChecking(false);
    }
  };
  return (
    <div className="stack-form">
      <label>
        출제일
        <input
          type="date"
          min={tomorrow}
          value={day}
          onChange={(e) => setDay(e.target.value)}
        />
      </label>
      {keys.map((key, i) => (
        <Picker
          key={i}
          kind="sets"
          label={`${i + 1}번째 판`}
          value={key}
          onChange={(value, _label, item) => {
            setKeys(keys.map((old, n) => (n === i ? value : old)));
            setSelected(
              selected.map((old, n) => (n === i ? (item as QuestionSet) : old)),
            );
          }}
        />
      ))}
      <p className="caption">
        같은 날은 서로 다른 15문제가 필요합니다. 저장 시 중복과 출제 가능 여부를
        다시 확인합니다. 해당 날짜의 기존 일정은 교체됩니다.
      </p>
      {duplicates && <p role="alert">선택한 세트 사이에 중복 문제가 있어요.</p>}
      <button
        className="btn primary"
        disabled={
          pending ||
          checking ||
          duplicates ||
          day < tomorrow ||
          !keys.every(Boolean) ||
          new Set(keys).size !== 3
        }
        onClick={submit}
      >
        편성 저장
      </button>
    </div>
  );
}
